import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Handshake, History, Scale, Building2 } from 'lucide-react';
import { formatCurrency, formatDate } from '../../lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Recovery status + Proposed Settlement (Josh 09/23).
//   lawyer path : Processing Legal → Collecting → Settled | Died in Settlement (comment required) → Closed
//   in-house    : Negotiation      → Collecting → Settled → Closed
// "Collecting" (Josh 10/12) = money is coming in; plain status, no popup.
// "Closed" (Josh 10/12) = the file is closed out; a popup asks for an optional note
// (e.g. "settled for less than the default calculation — lump sum offered").
// Choosing "Settled" opens the Proposed Settlement popup (amount + terms + date);
// saving it stores the agreement and sets the status. Nothing here posts to the
// ledger — payments keep arriving through the normal channels and the section
// tracks progress against the agreed amount.
const STATUS_BADGE = {
  'Processing Legal': 'default',
  'Negotiation': 'outline',
  'Collecting': 'default',
  'Settled': 'secondary',
  'Died in Settlement': 'destructive',
  'Closed': 'secondary',
};

const RecoveryStatusCard = ({ dealId, getAuthHeaders, refreshKey, onChanged }) => {
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [diedOpen, setDiedOpen] = useState(false);
  const [diedComment, setDiedComment] = useState('');
  const [closedOpen, setClosedOpen] = useState(false);
  const [closedNote, setClosedNote] = useState('');
  // Josh 09/24: "died in settlement would suffice enough to write off … use a pop up
  // modal to ask if its a write off" — shown right after the status is saved.
  const [writeOffOpen, setWriteOffOpen] = useState(false);
  const [writeOffView, setWriteOffView] = useState(null);

  const [settleOpen, setSettleOpen] = useState(false);
  const [settleForm, setSettleForm] = useState({ amount: '', terms: '', date: new Date().toISOString().slice(0, 10) });

  const [historyOpen, setHistoryOpen] = useState(false);

  const load = useCallback(async () => {
    if (!dealId) return;
    try {
      const r = await axios.get(`${API}/default-recovery/${dealId}/recovery`, { headers: getAuthHeaders() });
      setView(r.data);
    } catch (e) {
      console.error('recovery view failed', e);
    } finally {
      setLoading(false);
    }
  }, [dealId, getAuthHeaders]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const applyResult = (data) => {
    setView(data);
    if (onChanged) onChanged(data);
  };

  const postStatus = async (status, comment = '') => {
    setSaving(true);
    try {
      const r = await axios.post(`${API}/default-recovery/${dealId}/recovery-status`,
        { status, comment }, { headers: getAuthHeaders() });
      applyResult(r.data);
      toast.success(`Recovery status: ${status}`);
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not change recovery status');
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Posts the WRITE_OFF through the same endpoint as the "Write Off Remaining
  // Balance" button (routes/default_recovery.write_off_deal): closes the deal as
  // Paid in Full / Settled, locks it, and stamps the Legal Report fields.
  const confirmWriteOff = async () => {
    setSaving(true);
    try {
      const notes = `Died in Settlement — ${diedComment.trim()}`;
      const r = await axios.post(`${API}/default-recovery/${dealId}/write-off`, { notes }, { headers: getAuthHeaders() });
      toast.success(r.data?.message || 'Balance written off');
      setWriteOffOpen(false);
      await load();
      if (onChanged) onChanged({ written_off: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not write off the balance');
    } finally {
      setSaving(false);
    }
  };

  const onSelectStatus = (status) => {
    if (!view || status === view.recovery_status) return;
    if (status === 'Settled') {
      const ps = view.proposed_settlement || {};
      setSettleForm({
        amount: ps.amount != null ? String(ps.amount) : '',
        terms: ps.terms || '',
        date: ps.date || new Date().toISOString().slice(0, 10),
      });
      setSettleOpen(true);
      return;
    }
    if (status === 'Died in Settlement') {
      setDiedComment('');
      setDiedOpen(true);
      return;
    }
    if (status === 'Closed') {
      setClosedNote('');
      setClosedOpen(true);
      return;
    }
    postStatus(status);
  };

  const openReviseSettlement = () => {
    const ps = view?.proposed_settlement || {};
    setSettleForm({
      amount: ps.amount != null ? String(ps.amount) : '',
      terms: ps.terms || '',
      date: ps.date || new Date().toISOString().slice(0, 10),
    });
    setSettleOpen(true);
  };

  const saveProposedSettlement = async () => {
    const amount = parseFloat(settleForm.amount);
    if (!amount || amount <= 0) { toast.error('Enter the agreed settlement amount'); return; }
    setSaving(true);
    try {
      const r = await axios.post(`${API}/default-recovery/${dealId}/proposed-settlement`,
        { amount, terms: settleForm.terms, date: settleForm.date }, { headers: getAuthHeaders() });
      applyResult(r.data);
      setSettleOpen(false);
      toast.success(`Proposed settlement saved: ${formatCurrency(amount)}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save proposed settlement');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !view) return null;

  const ps = view.proposed_settlement;
  const prog = view.settlement_progress;
  const coll = view.collections || {};
  const isLawyer = view.recovery_mode === 'via_lawyer';

  return (
    <Card data-testid="recovery-status-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Handshake className="h-4 w-4" /> Recovery Status &amp; Settlement
            <Badge variant={STATUS_BADGE[view.recovery_status] || 'outline'} data-testid="recovery-status-badge">
              {view.recovery_status}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={view.recovery_status} onValueChange={onSelectStatus} disabled={saving}>
              <SelectTrigger className="w-[210px]" data-testid="recovery-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(view.recovery_status_options || []).map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)} title="Status history">
              <History className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1">
          {isLawyer ? <><Scale className="inline h-3 w-3 mr-1" />Lawyer path: Processing Legal → Collecting → Settled or Died in Settlement → Closed.</>
                    : <><Building2 className="inline h-3 w-3 mr-1" />In-house path: Negotiation → Collecting → Settled → Closed.</>}
          {' '}Changing this never posts to the ledger.
        </p>
        {view.recovery_status === 'Died in Settlement' && view.recovery_status_comment && (
          <p className="text-xs text-red-700 mt-1" data-testid="recovery-status-comment">Reason: {view.recovery_status_comment}</p>
        )}
        {view.recovery_status === 'Closed' && view.recovery_status_comment && (
          <p className="text-xs text-slate-300 mt-1 whitespace-pre-wrap" data-testid="recovery-status-note">Note: {view.recovery_status_comment}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Post-default collections split */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-muted/50 rounded-lg text-sm">
          <div>
            <p className="text-muted-foreground text-xs">Gross Recovered</p>
            <p className="font-semibold text-green-600" data-testid="rs-gross">{formatCurrency(coll.gross_collected)}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Via Lawyer</p>
            <p className="font-semibold" data-testid="rs-lawyer">{formatCurrency(coll.lawyer_collected)}</p>
          </div>
          <div>
            <p className="text-muted-foreground text-xs">In-House</p>
            <p className="font-semibold" data-testid="rs-in-house">{formatCurrency(coll.in_house_collected)}</p>
            {Number(coll.in_house_returned) > 0 && (
              <p className="text-[10px] text-red-600" data-testid="rs-in-house-returned">after {formatCurrency(coll.in_house_returned)} returned</p>
            )}
            {coll.recovery_mode_applied === 'via_lawyer' && Number(coll.in_house_excluded) > 0 && (
              <p className="text-[10px] text-muted-foreground" data-testid="rs-in-house-excluded">{coll.in_house_excluded_count} merchant pull{coll.in_house_excluded_count === 1 ? '' : 's'} ({formatCurrency(coll.in_house_excluded)}) since default not counted — deal is with the lawyer</p>
            )}
          </div>
          <div>
            <p className="text-muted-foreground text-xs">Net (after lawyer fees)</p>
            <p className="font-semibold" data-testid="rs-net">{formatCurrency(coll.net_recovered)}</p>
          </div>
        </div>
        {coll.cutoff_date && (
          <p className="text-[10px] text-muted-foreground -mt-2">Counts every cleared payment and lawyer collection dated on or after the default ({formatDate(coll.cutoff_date)}); a post-default pull that bounced is netted out. In-house and lawyer are one at a time: while the deal is with the lawyer, merchant pulls are not in-house recovery.</p>
        )}
        {coll.cutoff_missing && (
          <p className="text-[10px] text-amber-700 -mt-2" data-testid="rs-cutoff-missing">
            No default date on this deal (no defaulted_at and no Default Fee row), so in-house payments are not counted as recovery yet. Lawyer collections still are.
          </p>
        )}

        {/* Proposed settlement */}
        <div className="border rounded-lg p-3 space-y-2" data-testid="proposed-settlement-section">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">Proposed Settlement</p>
            <Button size="sm" variant={ps ? 'outline' : 'default'} onClick={openReviseSettlement} disabled={saving}
                    data-testid="proposed-settlement-btn">
              {ps ? 'Revise' : 'Record settlement'}
            </Button>
          </div>
          {!ps && <p className="text-xs text-muted-foreground">No agreement recorded yet. When you reach one, record the agreed amount and terms here — you can come back and revise it any time.</p>}
          {ps && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-muted-foreground text-xs">Agreed Amount</p>
                <p className="font-semibold" data-testid="ps-amount">{formatCurrency(ps.amount)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Agreed On</p>
                <p className="font-semibold">{formatDate(ps.date)}</p>
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Discount Given</p>
                <p className="font-semibold text-orange-600" data-testid="ps-discount">
                  {view.discount_given != null ? formatCurrency(view.discount_given) : '—'}
                </p>
                {ps.effective_balance_at_agreement != null && (
                  <p className="text-[10px] text-muted-foreground">balance was {formatCurrency(ps.effective_balance_at_agreement)}</p>
                )}
              </div>
              <div>
                <p className="text-muted-foreground text-xs">Collected Since</p>
                <p className="font-semibold text-green-600" data-testid="ps-collected">{formatCurrency(prog?.collected_since_agreement)}</p>
                <p className="text-[10px] text-muted-foreground">
                  {prog?.complete ? 'settlement complete' : `${formatCurrency(prog?.remaining)} remaining${prog?.percent_complete != null ? ` · ${prog.percent_complete}%` : ''}`}
                </p>
              </div>
              {ps.terms && (
                <div className="col-span-2 sm:col-span-4">
                  <p className="text-muted-foreground text-xs">Terms</p>
                  <p className="text-sm whitespace-pre-wrap" data-testid="ps-terms">{ps.terms}</p>
                </div>
              )}
              {prog?.complete && (
                <div className="col-span-2 sm:col-span-4 p-2 bg-emerald-50 border border-emerald-200 rounded text-xs text-emerald-800">
                  Collections have reached the agreed amount. Use “Write Off Remaining Balance” below to close the deal and write off the discount.
                </div>
              )}
              {(ps.history || []).length > 0 && (
                <p className="col-span-2 sm:col-span-4 text-[10px] text-muted-foreground">
                  {ps.history.length} earlier version{ps.history.length === 1 ? '' : 's'} — see history.
                </p>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {/* Died in Settlement — comment required */}
      <Dialog open={diedOpen} onOpenChange={setDiedOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Died in Settlement</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Why did it die? (required)</Label>
            <Textarea value={diedComment} onChange={e => setDiedComment(e.target.value)} rows={3}
                      placeholder="e.g. bankruptcy filed, merchant never answered, not worth pursuing…" data-testid="died-comment-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDiedOpen(false)}>Cancel</Button>
            <Button variant="destructive" disabled={saving || !diedComment.trim()}
                    onClick={async () => {
                      const data = await postStatus('Died in Settlement', diedComment);
                      if (!data) return;
                      setDiedOpen(false);
                      if (data.write_off_eligible) { setWriteOffView(data); setWriteOffOpen(true); }
                    }}
                    data-testid="died-confirm-btn">
              Mark Died in Settlement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Closed — optional note (Josh 10/12: "write notes pertaining to the deal") */}
      <Dialog open={closedOpen} onOpenChange={setClosedOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Close out this recovery file</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Note (optional) — why / how it closed</Label>
            <Textarea value={closedNote} onChange={e => setClosedNote(e.target.value)} rows={4}
                      placeholder="e.g. settled for less than the default calculation — merchant offered a lump sum…" data-testid="closed-note-input" />
            <p className="text-[11px] text-muted-foreground">Saved on the deal and in the recovery history. Posts nothing to the ledger; the deal status does not change.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setClosedOpen(false)}>Cancel</Button>
            <Button disabled={saving}
                    onClick={async () => {
                      const data = await postStatus('Closed', closedNote);
                      if (!data) return;
                      setClosedOpen(false);
                    }}
                    data-testid="closed-confirm-btn">
              Mark Closed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Died in Settlement → write off? */}
      <Dialog open={writeOffOpen} onOpenChange={setWriteOffOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Write off the remaining balance?</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm">
            <p>
              Remaining effective balance: <span className="font-semibold" data-testid="wo-amount">{formatCurrency(writeOffView?.effective_balance)}</span>.
            </p>
            <p className="text-xs text-muted-foreground">
              Yes posts a WRITE_OFF for that amount, closes the deal as Paid in Full / Settled (locked), and the
              write-off shows on the Legal Report and portfolio totals. “Not now” keeps the deal in Default with the
              Died in Settlement status — you can still write it off later from the Write-Off card.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWriteOffOpen(false)} disabled={saving} data-testid="wo-not-now-btn">Not now</Button>
            <Button variant="destructive" onClick={confirmWriteOff} disabled={saving} data-testid="wo-confirm-btn">
              {saving ? 'Posting…' : `Yes, write off ${formatCurrency(writeOffView?.effective_balance)}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Proposed settlement popup */}
      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Proposed Settlement</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">
              Current effective balance: <span className="font-semibold">{formatCurrency(view.effective_balance)}</span>.
              Record what was agreed — this does not post any transaction.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Agreed amount ($)</Label>
                <Input type="number" min="0" step="0.01" value={settleForm.amount}
                       onChange={e => setSettleForm(p => ({ ...p, amount: e.target.value }))} data-testid="ps-amount-input" />
              </div>
              <div>
                <Label className="text-xs">Agreement date</Label>
                <Input type="date" value={settleForm.date}
                       onChange={e => setSettleForm(p => ({ ...p, date: e.target.value }))} data-testid="ps-date-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Terms of the agreement</Label>
              <Textarea rows={4} value={settleForm.terms}
                        onChange={e => setSettleForm(p => ({ ...p, terms: e.target.value }))}
                        placeholder="e.g. $12,000 lump sum by 10/15; or $500 weekly starting 10/01 until paid…" data-testid="ps-terms-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettleOpen(false)}>Cancel</Button>
            <Button disabled={saving} onClick={saveProposedSettlement} data-testid="ps-save-btn">
              {saving ? 'Saving…' : 'Save & mark Settled'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Recovery history</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm max-h-[60vh] overflow-y-auto">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Status changes</p>
              {(view.recovery_status_history || []).length === 0 && <p className="text-xs text-muted-foreground">None yet.</p>}
              {[...(view.recovery_status_history || [])].reverse().map((h, i) => (
                <div key={i} className="flex justify-between gap-3 border-b py-1">
                  <span>{h.status}{h.auto ? <span className="text-[10px] text-muted-foreground"> (auto)</span> : ''}{h.comment ? <span className="text-muted-foreground"> — {h.comment}</span> : ''}</span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDate(h.at)}</span>
                </div>
              ))}
            </div>
            {ps && (ps.history || []).length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Earlier proposed settlements</p>
                {[...ps.history].reverse().map((h, i) => (
                  <div key={i} className="border-b py-1">
                    <div className="flex justify-between gap-3">
                      <span className="font-medium">{formatCurrency(h.amount)}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(h.date)} · set {formatDate(h.set_at)}</span>
                    </div>
                    {h.terms && <p className="text-xs text-muted-foreground whitespace-pre-wrap">{h.terms}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default RecoveryStatusCard;
